// utils/anthropometry/classifications.js

const classifyChildBMI = (zScore) => {
  if (zScore > 2) return 'Overweight';
  if (zScore >= -2 && zScore <= 2) return 'Adequate weight';
  if (zScore < -2) return 'Underweight';
  return 'Undetermined';
};

const classifyElderlyBMI = (bmi) => {
  if (bmi < 22) return 'Underweight';
  if (bmi >= 22 && bmi <= 27) return 'Normal';
  if (bmi > 27) return 'Overweight';
  return 'Undetermined';
};

const classifyBMI = (bmi, zScore, patientGroup) => {
  if (patientGroup === '2') {
    return zScore ? classifyChildBMI(zScore) : 'z-Score not provided';
  }

  if (patientGroup === '4') {
    return classifyElderlyBMI(bmi);
  }

  if (bmi < 16) return 'Severe Thinness Grade III';
  if (bmi < 17) return 'Thinness Grade II';
  if (bmi < 18.5) return 'Thinness Grade I';
  if (bmi < 25) return 'Normal';
  if (bmi < 30) return 'Overweight';
  if (bmi < 35) return 'Obesity Grade I';
  if (bmi < 40) return 'Obesity Grade II';
  return 'Obesity Grade III';
};

const classifyBray = (bmi) => {
  if (bmi < 16) return 'Severe underweight grade III';
  if (bmi <= 16.9) return 'Severe underweight grade II';
  if (bmi <= 18.4) return 'Underweight grade I';
  if (bmi <= 24.9) return 'Normal';
  if (bmi <= 29.9) return 'Overweight';
  if (bmi <= 34.9) return 'Obesity grade I';
  if (bmi <= 39.9) return 'Obesity grade II';
  return 'Obesity grade III';
};

const classifyBlackburn = (ptPercent) => {
  if (ptPercent >= 91) return 'Normal';
  if (ptPercent >= 80) return 'Mild depletion';
  if (ptPercent >= 70) return 'Moderate depletion';
  if (ptPercent < 70) return 'Severe depletion';
  return 'Undetermined';
};

const classifyBiotype = (height, gender) => {
  if (gender === 'male') {
    if (height <= 155) return 'Short-limbed';
    if (height <= 170) return 'Normal-limbed';
    return 'Long-limbed';
  }
  if (gender === 'female') {
    if (height <= 150) return 'Short-limbed';
    if (height <= 165) return 'Normal-limbed';
    return 'Long-limbed';
  }
  return 'Undetermined';
};

const classifyWaistCircumference = (waist, gender) => {
  const w = Number(waist);
  if (gender === 'male') {
    if (w < 94) return 'No risk';
    if (w <= 102) return 'Increased risk';
    return 'High risk';
  }
  if (gender === 'female') {
    if (w < 80) return 'No risk';
    if (w <= 88) return 'Increased risk';
    return 'High risk';
  }
  return 'Undetermined';
};

const classifyInternalFatPercentage = (percentage) => {
  return percentage > 25 ? 'High' : 'Normal';
};

const calculateMuscleDiagnosis = (bmi, fatPercentage, muscleClassification) => {
  if (bmi > 30) return 'Obesity';
  if (fatPercentage > 25) return 'Excess fat';
  if (muscleClassification === 'Below average') return 'Low muscle mass';
  return 'Normal';
};
const classifyMuscleMass = (armMuscleArea, gender) => {
  if (gender === 'male') {
    if (armMuscleArea <= 35) return 'Low muscle mass';
    if (armMuscleArea > 35 && armMuscleArea <= 45) return 'Below average';
    if (armMuscleArea > 45 && armMuscleArea <= 60) return 'Average';
    if (armMuscleArea > 60 && armMuscleArea <= 75) return 'Above average';
    if (armMuscleArea > 75) return 'High muscle mass';
  } else {
    if (armMuscleArea <= 20) return 'Low muscle mass';
    if (armMuscleArea > 20 && armMuscleArea <= 25) return 'Below average';
    if (armMuscleArea > 25 && armMuscleArea <= 35) return 'Average';
    if (armMuscleArea > 35 && armMuscleArea <= 55) return 'Above average';
    if (armMuscleArea > 55) return 'High muscle mass';
  }
  return 'Undetermined';
};

const classifyActivityFactor = (physicalActivityLevel, gender) => {
  if (gender === 'male') {
    if (physicalActivityLevel === "light") return 1.55;    // Light physical activity
    if (physicalActivityLevel === "moderate") return 1.78; // Moderate physical activity
    if (physicalActivityLevel === "intense") return 2.10;  // Intense physical activity
  } else if (gender === 'female') {
    if (physicalActivityLevel === "light") return 1.56;    // Light physical activity
    if (physicalActivityLevel === "moderate") return 1.64; // Moderate physical activity
    if (physicalActivityLevel === "intense") return 1.82;  // Intense physical activity
  }

  console.error("Invalid activity factor or gender");
  return 0; // Returns 0 if unable to classify
};

// Function to Classify Activity Frequency with useCallback - Values Based on CDC (Centers for Disease Control and Prevention) and ACSM (American College of Sports Medicine):
const classifyActivityLevel = (frequencyOfActivity) => {
  if (frequencyOfActivity === "unable") return 1.2;      // Sedentary (little or no physical activity)
  if (frequencyOfActivity === "sedentary") return 1.375; // Light (light exercise or activity 1-3 days per week)
  if (frequencyOfActivity === "occasional") return 1.55; // Moderate (moderate exercise 3-5 days per week)
  if (frequencyOfActivity === "frequent") return 1.725;  // Active (intense exercise 6-7 days per week)
  if (frequencyOfActivity === "regular") return 1.9;     // Very Active (very intense exercises or heavy physical activity)

  console.error("Invalid activity frequency");
  return 0; // Returns 0 if unable to classify
};

const classifyLeanBodyMass = (leanMass, gender) => {
  if (gender === 'male') return leanMass < 50 ? 'Low' : 'Normal';
  return leanMass < 40 ? 'Low' : 'Normal';
};

const classifyBernadinelli = (index) => {
  if (index >= 1.9) return 'Long-limbed';       // Longilíneo
  if (index >= 1.7) return 'Normal';            // Mesolíneo
  return 'Short-limbed';                        // Brevilíneo
};

const classificarIMC = (imc, zScore, grupoPaciente) => {
  if (grupoPaciente === '2') {
    return zScore ? classificarIMCCrianca(zScore) : 'z-Score não fornecido';
  }

  if (grupoPaciente === '4') {
    return classificarIMCIdoso(imc);
  }

  if (imc < 16) return 'Magreza Grave (Grau III)';
  if (imc < 17) return 'Magreza Moderada (Grau II)';
  if (imc < 18.5) return 'Magreza Leve (Grau I)';
  if (imc < 25) return 'Peso Normal';
  if (imc < 30) return 'Sobrepeso';
  if (imc < 35) return 'Obesidade Grau I';
  if (imc < 40) return 'Obesidade Grau II';
  return 'Obesidade Grau III';
};

const classificarIMCCrianca = (zScore) => {
  if (zScore > 2) return 'Sobrepeso';
  if (zScore >= -2 && zScore <= 2) return 'Peso adequado';
  if (zScore < -2) return 'Baixo peso';
  return 'Indeterminado';
};

const classificarIMCIdoso = (imc) => {
  if (imc < 22) return 'Baixo peso';
  if (imc >= 22 && imc <= 27) return 'Peso normal';
  if (imc > 27) return 'Sobrepeso';
  return 'Indeterminado';
};

const classificarBray = (imc) => {
  if (imc < 16) return 'Magreza grave (Grau III)';
  if (imc <= 16.9) return 'Magreza grave (Grau II)';
  if (imc <= 18.4) return 'Magreza (Grau I)';
  if (imc <= 24.9) return 'Peso normal';
  if (imc <= 29.9) return 'Sobrepeso';
  if (imc <= 34.9) return 'Obesidade Grau I';
  if (imc <= 39.9) return 'Obesidade Grau II';
  return 'Obesidade Grau III';
};
const classificarBlackburn = (percentualPT) => {
  if (percentualPT >= 91) return 'Normal';
  if (percentualPT >= 80) return 'Depleção leve';
  if (percentualPT >= 70) return 'Depleção moderada';
  if (percentualPT < 70) return 'Depleção grave';
  return 'Indeterminado';
};

const classificarBiotipo = (altura, genero) => {
  if (genero === 'male') {
    if (altura <= 155) return 'Membros curtos';
    if (altura <= 170) return 'Membros normais';
    return 'Membros longos';
  }
  if (genero === 'female') {
    if (altura <= 150) return 'Membros curtos';
    if (altura <= 165) return 'Membros normais';
    return 'Membros longos';
  }
  return 'Indeterminado';
};


const classificarCircunferenciaCintura = (cintura, genero) => {
  const c = Number(cintura);
  if (genero === 'male') {
    if (c < 94) return 'Sem risco';
    if (c <= 102) return 'Risco aumentado';
    return 'Risco muito aumentado';
  }
  if (genero === 'female') {
    if (c < 80) return 'Sem risco';
    if (c <= 88) return 'Risco aumentado';
    return 'Risco muito aumentado';
  }
  return 'Indeterminado';
};

const classificarPercentualGorduraVisceral = (percentual) => {
  return percentual > 25 ? 'Elevado' : 'Normal';
};

const diagnosticoMuscular = (imc, percentualGordura, classificacaoMuscular) => {
  if (imc > 30) return 'Obesidade';
  if (percentualGordura > 25) return 'Excesso de gordura';
  if (classificacaoMuscular === 'Abaixo da média') return 'Baixa massa muscular';
  return 'Normal';
};
const classificarMassaMuscular = (areaMuscularBraco, genero) => {
  const a = Number(areaMuscularBraco);

  if (genero === 'male') {
    if (a <= 35) return 'Baixa massa muscular';
    if (a > 35 && a <= 45) return 'Abaixo da média';
    if (a > 45 && a <= 60) return 'Média';
    if (a > 60 && a <= 75) return 'Acima da média';
    if (a > 75) return 'Alta massa muscular';
  } else {
    if (a <= 20) return 'Baixa massa muscular';
    if (a > 20 && a <= 25) return 'Abaixo da média';
    if (a > 25 && a <= 35) return 'Média';
    if (a > 35 && a <= 55) return 'Acima da média';
    if (a > 55) return 'Alta massa muscular';
  }

  return 'Indeterminado';
};

const classificarFatorAtividade = (nivelAtividadeFisica, genero) => {
  if (genero === 'male') {
    if (nivelAtividadeFisica === "light") return 1.55;    // Atividade física leve
    if (nivelAtividadeFisica === "moderate") return 1.78; // Atividade física moderada
    if (nivelAtividadeFisica === "intense") return 2.10;  // Atividade física intensa
  } else if (genero === 'female') {
    if (nivelAtividadeFisica === "light") return 1.56;    // Atividade física leve
    if (nivelAtividadeFisica === "moderate") return 1.64; // Atividade física moderada
    if (nivelAtividadeFisica === "intense") return 1.82;  // Atividade física intensa
  }

  console.error("Fator de atividade ou gênero inválido");
  return 0; // Retorna 0 se não for possível classificar
};

// Função para classificar o nível de atividade física com base na frequência semanal
// Valores baseados no CDC (Centers for Disease Control and Prevention) e ACSM

const classificarNivelAtividade = (frequenciaAtividade) => {
  if (frequenciaAtividade === "unable") return 1.2;        // Inativo (nenhuma ou quase nenhuma atividade física)
  if (frequenciaAtividade === "sedentary") return 1.375;   // Leve (exercício leve de 1 a 3 vezes por semana)
  if (frequenciaAtividade === "occasional") return 1.55;   // Moderado (exercício moderado de 3 a 5 vezes por semana)
  if (frequenciaAtividade === "frequent") return 1.725;    // Ativo (exercício intenso de 6 a 7 vezes por semana)
  if (frequenciaAtividade === "regular") return 1.9;       // Muito Ativo (exercícios muito intensos ou trabalho físico pesado)

  console.error("Frequência de atividade inválida");
  return 0; // Retorna 0 se não for possível classificar
};

const classificarMassaMagra = (massaMagra, genero) => {
  if (genero === 'male') return massaMagra < 50 ? 'Baixa' : 'Normal';
  return massaMagra < 40 ? 'Baixa' : 'Normal';
};
const classificarBernadinelli = (indice) => {
  if (indice >= 1.9) return 'Longilíneo';   // Membros longos
  if (indice >= 1.7) return 'Mesolíneo';    // Proporções normais
  return 'Brevilíneo';                      // Membros curtos
};


module.exports = {
  classifyBMI,
  classifyChildBMI,
  classifyElderlyBMI,
  classifyBray,
  classifyBlackburn,
  classifyBiotype,
  classifyWaistCircumference,
  classifyInternalFatPercentage,
  calculateMuscleDiagnosis,
  classifyMuscleMass,
  classifyActivityFactor,
  classifyActivityLevel,
  classifyLeanBodyMass,
  classifyBernadinelli,
  classificarIMC,
  classificarIMCCrianca,
  classificarIMCIdoso,
  classifyBMI,
  classificarBray,
  classificarBlackburn,
  classificarBiotipo,
  classificarCircunferenciaCintura,
  classificarPercentualGorduraVisceral,
  diagnosticoMuscular,
  classificarMassaMuscular,
  classificarFatorAtividade,
  classificarNivelAtividade,
  classificarMassaMagra,
  classificarBernadinelli,

};
